<script setup lang="ts">
import { ref } from 'vue';

interface Props {
  maxFileSize?: number;
  acceptedFormats?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  maxFileSize: 500 * 1024, // 500KB in bytes
  acceptedFormats: () => ['.svg']
});

const emit = defineEmits<{
  uploaded: [file: File, response: any];
  error: [message: string];
}>();

const isDragging = ref(false);
const isUploading = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

const validateFile = (file: File): string | null => {
  if (!file.type.includes('svg') && !file.name.toLowerCase().endsWith('.svg')) {
    return 'Please select a valid SVG file.';
  }

  if (file.size > props.maxFileSize) {
    return `File size must be less than ${Math.round(props.maxFileSize / 1024)}KB.`;
  }

  return null;
};

const uploadFile = async (file: File) => {
  const validationError = validateFile(file);
  if (validationError) {
    emit('error', validationError);
    return;
  }

  isUploading.value = true;

  try {
    const formData = new FormData();
    formData.append('icon', file);

    const response = await axios.post(route('api.manage-whitelabel-app.custom-icon.store'), formData);

    if (response.status === 200) {
      emit('uploaded', file, response.data);
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Upload failed';
    emit('error', errorMessage);
  } finally {
    isUploading.value = false;
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    uploadFile(file);
  }
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = false;

  const file = event.dataTransfer?.files[0];
  if (file) {
    uploadFile(file);
  }
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  if (!event.relatedTarget || !(event.currentTarget as Element).contains(event.relatedTarget as Node)) {
    isDragging.value = false;
  }
};

const triggerFileInput = () => {
  fileInput.value?.click();
};
</script>

<template>
  <div class="custom-icon-selector bg-white">
    <div
      class="upload-area border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200"
      :class="{
        'border-blue-400 bg-blue-50': isDragging,
        'border-gray-300 hover:border-gray-400': !isDragging && !isUploading,
        'border-gray-200 bg-gray-50': isUploading
      }"
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        accept=".svg"
        class="hidden"
        @change="handleFileSelect"
        :disabled="isUploading"
      />

      <div class="space-y-4">

        <div>
          <p class="text-lg font-medium text-gray-700" v-if="!isUploading">
            Drop SVG file here or click to browse
          </p>
          <p class="text-lg font-medium text-gray-700" v-else>
            Uploading...
          </p>
          <p class="text-sm text-gray-500 mt-2">
            Maximum file size: {{ Math.round(maxFileSize / 1024) }}KB • SVG format only
          </p>
        </div>

        <button
          type="button"
          class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          @click.stop="triggerFileInput"
          :disabled="isUploading"
        >
          {{ isUploading ? 'Uploading...' : 'Choose File' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.upload-area {
  cursor: pointer;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area:hover {
  background-color: rgba(249, 250, 251, 0.5);
}
</style>
